import axios from "axios";
import { handleError } from "./utils";

const BASE_URL = "/api/matdagboken";

const api = axios.create({
    baseURL: BASE_URL,
    headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
    },
});

// MAIN DIARY DATA

export const getDiaryData = async (currentDate) => {
    try {
        const res = await api.get(`/${currentDate}`);

        return res.data;
    } catch (error) {
        handleError(error, "Failed to fetch diary data");
    }
};

// SEARCH FUNCTIONS

export const getDiaryDataBySearch = async (data, signal) => {
    const { query, db1, db2 } = data;

    const endpoint = `/search/${encodeURIComponent(query)}`;

    const res = await api.get(endpoint, {
        params: { db1, db2 },
        signal,
    });

    return res.data;
};

export const getDiaryLoadmore = async (data, signal) => {

    const { query, ...rest } = data;

    const endpoint = `/loadmore/${encodeURIComponent(query)}`;

    const res = await api.get(endpoint, {
        params: rest,
        signal,
    });

    return res;
};

export const getFoodItemsByBarcode = async (ean) => {
    const res = await api.get(`/barcode/${ean}`);
    return res.data;
};

// MEAL FUNCTIONS

export const saveMeal = async (mealData) => {
    try {
        const res = await api.post("/meal/store", mealData);

        return res.data;
    } catch (error) {
        handleError(error, "Failed to save meal");
    }
};

export const updateMeal = async (mealId, mealData) => {
    try {
        const res = await api.patch(`/meal/update/${mealId}`, mealData);

        return res.data;
    } catch (error) {
        handleError(error, "Failed to update meal");
    }
};

export const deleteMeal = async (mealId) => {
    try {
        const res = await api.delete(`/meal/delete/${mealId}`);

        return res.data;
    } catch (error) {
        handleError(error, "Failed to delete meal");
    }
};

export const copyMeal = async ({ meal_type, from_date, to_date, id }) => {
    try {
        const res = await api.post("/meal/copy", { id, meal_type, from_date, to_date });

        return res.data;
    } catch (error) {
        handleError(error, "Failed to copy meal");
    }
};

export const moveMeal = async ({ meal_type, from_date, to_date, id }) => {
    try {
        const res = await api.patch("/meal/move", { id, meal_type, from_date, to_date });

        return res.data;
    } catch (error) {
        handleError(error, "Failed to move meal");
    }
};

// FAVORITE MEAL FUNCTIONS

export const getFavoriteMeal = async ({ favoriteId }) => {
    try {
        const res = await api.get(`/favorite/${favoriteId}`);

        return res.data;
    } catch (error) {
        handleError(error, "Failed to fetch favorite meal");
    }
};

export const createFavoriteMeal = async ({ mealId, name }) => {
    try {
        const res = await api.post("/favorite/store", {
            meal_id: mealId,
            name,
        });

        return res.data;
    } catch (error) {
        handleError(error, "Failed to create favorite meal");
    }
};

export const addFavoriteMeal = async ({ favoriteId, date, mealType, name }) => {
    try {
        const res = await api.post(`/favorite/add/${favoriteId}`, {
            date,
            meal_type: mealType,
            name,
        });

        return res.data;
    } catch (error) {
        handleError(error, "Failed to add favorite meal");
    }
};

export const updateFavoriteMeal = async ({ id, name, items }) => {
    try {
        const res = await api.patch(`/favorite/update/${id}`, {
            name,
            items,
        });

        return res.data;
    } catch (error) {
        handleError(error, "Failed to update favorite meal");
    }
};

export const deleteFavoriteMeal = async (favoriteId) => {
    try {
        const res = await api.delete(`/favorite/delete/${favoriteId}`);

        return res.data;
    } catch (error) {
        handleError(error, "Failed to delete favorite meal");
    }
};

// MEAL DATES

export const getMealDates = async (from, to, mealType) => {
    try {
        const params = {};

        if (from) params.from = from;
        if (to) params.to = to;

        if (mealType !== undefined && mealType !== null) {
            params.meal_type = Number(mealType);
        }

        const res = await api.get("/meal/dates", { params });

        return Array.isArray(res.data?.dates) ? res.data.dates : [];
    } catch (error) {
        handleError(error, "Failed to fetch meal dates");
    }
};

// EXERCISE FUNCTIONS

export const saveExercise = async (exerciseData) => {
    try {
        const res = await api.post("/exercise/store", exerciseData);

        return res.data;
    } catch (error) {
        handleError(error, "Failed to save exercise");
    }
};

export const updateExercise = async (exerciseId, exerciseData) => {
    try {
        const res = await api.patch(`/exercise/${exerciseId}`, exerciseData);

        return res.data;
    } catch (error) {
        handleError(error, "Failed to update exercise");
    }
};

export const deleteExercise = async (exerciseId) => {
    try {
        const res = await api.delete(`/exercise/${exerciseId}`);

        return res.data;
    } catch (error) {
        handleError(error, "Failed to delete exercise");
    }
};

export const deleteExercises = async (date) => {
    try {
        const res = await api.delete(`/exercise/date/${date}`);

        return res.data;
    } catch (error) {
        handleError(error, "Failed to delete exercises for date");
    }
};

export const getExerciseTypes = async () => {
    try {
        const res = await api.get("/exercise/types");

        return res.data;
    } catch (error) {
        handleError(error, "Failed to fetch exercise types");
    }
};

// EXERCISE SESSION FUNCTIONS

export const copyExerciseSession = async ({ from_date, to_date }) => {
    try {
        const res = await api.post("/exercise-session/copy", {
            from_date,
            to_date,
        });

        return res.data;
    } catch (error) {
        handleError(error, "Failed to copy exercise session");
    }
};

export const moveExerciseSession = async ({ from_date, to_date }) => {
    try {
        const res = await api.post("/exercise-session/move", {
            from_date,
            to_date,
        });

        return res.data;
    } catch (error) {
        handleError(error, "Failed to move exercise session");
    }
};

export const deleteExerciseSession = async (date) => {
    try {
        const res = await api.delete(`/exercise-session/delete/${date}`);

        return res.data;
    } catch (error) {
        handleError(error, "Failed to delete exercise session");
    }
};

export const getExerciseSessionDates = async (from, to) => {
    try {
        const params = {};
        if (from) params.from = from;
        if (to) params.to = to;

        const res = await api.get("/exercise-session/dates", { params });

        return Array.isArray(res.data?.dates) ? res.data.dates : [];
    } catch (error) {
        handleError(error, "Failed to fetch exercise session dates");
    }
};

// FAVORITE EXERCISE SESSIONS

export const getFavoriteExerciseSessions = async () => {
    try {
        const res = await api.get("/favorite-exercise-sessions");
        return res.data?.favorites ?? [];
    } catch (error) {
        handleError(error, "Failed to fetch favorite exercise sessions");
    }
};

export const createFavoriteExerciseSession = async ({ sessionId, name, comment }) => {
    try {
        const res = await api.post(`/favorite-exercise-sessions/from-session/${sessionId}`, {
            name,
            comment,
        });

        return res.data;
    } catch (error) {
        handleError(error, "Failed to create favorite exercise session");
    }
};

export const applyFavoriteExerciseSession = async ({ favoriteId, date }) => {
    try {
        const res = await api.post(`/favorite-exercise-sessions/${favoriteId}/apply`, { date });

        return res.data;
    } catch (error) {
        handleError(error, "Failed to apply favorite exercise session");
    }
};

export const deleteFavoriteExerciseSession = async (favoriteId) => {
    try {
        const res = await api.delete(`/favorite-exercise-sessions/${favoriteId}`);
        return res.data;
    } catch (error) {
        handleError(error, "Failed to delete favorite exercise session");
    }
};

// EDITING FAVORITE EXERCISE SESSIONS

export const updateFavoriteExerciseSession = async ({ favoriteId, data }) => {
    try {
        const res = await api.patch(`/favorite-exercise-sessions/${favoriteId}`, data);

        return res.data;
    } catch (error) {
        handleError(error, "Failed to update favorite exercise session");
    }
};

export const updateFavoriteExerciseSessionItem = async ({ itemId, data }) => {
    try {
        const res = await api.patch(`/favorite-exercise-sessions/items/${itemId}`, data);

        return res.data;
    } catch (error) {
        handleError(error, "Failed to update favorite exercise item");
    }
};

export const deleteFavoriteExerciseSessionItem = async (itemId) => {
    try {
        const res = await api.delete(`/favorite-exercise-sessions/items/${itemId}`);

        return res.data;
    } catch (error) {
        handleError(error, "Failed to delete favorite exercise item");
    }
};
